import * as ko from 'knockout'
import ObservableRaceReplay from '@classic/Betting-v2/Model/Observables/ObservableRaceReplay'
import { Disposable } from '@classic/AppUtils/Framework/Disposable/Disposable'
import * as EventAggregator from '@classic/AppUtils/Framework/Messaging/EventAggregator'
import {
  trackRaceReplayVideoClosed,
  trackRaceReplayVideoLaunched,
  trackRaceReplayVideoMute,
} from '@core/Areas/Racing/Components/RaceReplay/analytics'
import { post } from '@classic/Foundation/Services/ApiService'
import { showLogin } from '@core/Areas/Login/helpers'

export interface Params {
  id: string
  raceReplayData: ObservableRaceReplay
  source: string
}

export class RaceReplayViewModel extends Disposable {
  public id!: string
  public videoSource!: ko.Observable<string>
  public message!: ko.Observable<string>
  public raceReplayData!: ObservableRaceReplay

  private isMuted = false
  private source = ''

  constructor(params: Params) {
    super()
    this.id = params.id
    this.raceReplayData = params.raceReplayData
    this.source = params.source
    this.videoSource = ko.observable('')
    this.message = ko.observable('')

    this.safeSubscribe(`toggle-race-replay-for-${this.id}`, () => {
      this.toggleVideo()
    })
    this.safeSubscribe(`stop-race-replay-for-${this.id}`, () => {
      if (this.isVideoOn()) {
        this.closeVideo()
      }
    })
    this.safeSubscribe(`stop-all-race-replay`, () => {
      if (this.isVideoOn()) {
        this.closeVideo()
      }
    })
  }

  public visible = (): boolean => {
    return this.isVideoOn() || this.shouldDisplayMessage()
  }

  public isVideoOn = (): boolean => {
    return this.videoSource() != null && this.videoSource().length > 0
  }

  public shouldDisplayMessage = (): boolean => {
    return this.message() != null && this.message().length > 0
  }

  public closeVideo = (): void => {
    this.message('')
    this.videoSource('')
    EventAggregator.publish(`race-replay-stopped-for-${this.id}`)
    trackRaceReplayVideoClosed()
  }

  public trackVideoPlay = (): void => {
    const replayLocation = this.source
    trackRaceReplayVideoLaunched(replayLocation, true)
  }

  public trackVolumeChange = (_: unknown, event: Event): void => {
    const target = event.currentTarget as HTMLVideoElement

    const isMutedNew = target.volume <= 0.1 || target.muted
    if (this.isMuted != isMutedNew) trackRaceReplayVideoMute(isMutedNew)
    this.isMuted = isMutedNew
  }

  public toggleVideo = (): void => {
    if (this.isVideoOn()) {
      this.closeVideo()
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const promise = post<any>({
        url: '/api/videoentitlement',
        body: { encrpytedVideoKey: this.raceReplayData.videoKey() },
      })

      promise
        .then(model => {
          if (model.IsEntitled) {
            EventAggregator.publish(`stop-all-race-replay`)
            this.message('')
            this.videoSource(model.VideoKey)
            EventAggregator.publish(`race-replay-started-for-${this.id}`)
          } else {
            if (model.CallToActionLoginRequired) {
              showLogin()
            } else {
              this.message(model.ReasonText)
            }
          }
        })
        .catch(() => {
          this.videoSource('')
          this.message('Error checking entitlement to replay.')
        })
    }
  }
}
